// packages/web/src/pages/SubscriptionPlansPage.tsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

// Styled Components matching LandingPage
const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  padding: theme.spacing(8, 0, 4, 0),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 0, 1.5, 0),
  },
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: theme.spacing(1.5),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: '#666',
  marginBottom: theme.spacing(4),
  lineHeight: 1.6,
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
  },
}));

const PlansSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0, 6, 0),
  backgroundColor: '#ffffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0, 3, 0),
  },
}));

const PlanCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'featured'
})<{ featured?: boolean }>(({ theme, featured }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  border: '1px solid #e5e5e5',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(featured && {
    border: '2px solid #c4d600',
    boxShadow: '0 4px 12px rgba(196, 214, 0, 0.15)',
    [theme.breakpoints.up('md')]: {
      transform: 'scale(1.05)',
      zIndex: 1,
    },
  }),
  '&:hover': {
    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
    borderColor: '#c4d600',
    [theme.breakpoints.up('md')]: {
      transform: featured ? 'scale(1.07)' : 'translateY(-6px)',
    },
    [theme.breakpoints.down('sm')]: {
      transform: 'none',
    },
  },
}));

const FeaturedBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontWeight: 700,
  fontSize: '0.75rem',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '20px',
  marginBottom: theme.spacing(1.5),
  '& svg': {
    fontSize: '1rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: theme.spacing(0.4, 1.25),
  },
}));

const PlanIconWrapper = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '10px',
  backgroundColor: '#f0f8e8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(1.5),
  '& svg': {
    fontSize: 24,
    color: '#84b817',
  },
  [theme.breakpoints.down('sm')]: {
    width: 36,
    height: 36,
    marginBottom: theme.spacing(1),
    '& svg': {
      fontSize: 20,
    },
  },
}));

const PlanTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.75),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(0.5),
  },
}));

const PlanDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#666',
  marginBottom: theme.spacing(1.5),
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(1),
  },
}));

const PriceSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1.5, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0),
  },
}));

const Price = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#84b817',
  lineHeight: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const PricePeriod = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: '#666',
  marginTop: theme.spacing(0.5),
  fontWeight: 400,
}));

const FeatureList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(1.5, 1.5, 1, 1.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5, 0.5, 1.5),
  },
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.375, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.25, 0),
  },
}));

const FeatureIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 28,
  '& svg': {
    color: '#84b817',
    fontSize: 16,
  },
}));

const FeatureText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontSize: '0.85rem',
    color: '#1a1a1a',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiListItemText-primary': {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 4),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  border: 'none',
  width: '100%',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.25, 3),
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 4),
  borderRadius: '8px',
  border: '2px solid #1a1a1a',
  textTransform: 'none',
  width: '100%',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.25, 3),
  },
}));

const ComingSoonButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#e5e5e5',
  color: '#999',
  fontSize: '1rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 4),
  borderRadius: '8px',
  textTransform: 'none',
  width: '100%',
  cursor: 'not-allowed',
  '&:hover': {
    backgroundColor: '#e5e5e5',
  },
  '&.Mui-disabled': {
    backgroundColor: '#e5e5e5',
    color: '#999',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.25, 3),
  },
}));


const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    marginBottom: theme.spacing(3),
  },
}));


export const SubscriptionPlansPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleGetStarted = () => {
    if (!user) {
      setLoginDialogOpen(true);
    } else {
      window.location.href = '/';
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free Player',
      price: 'Free',
      period: 'Forever',
      description: 'Perfect for casual tennis players',
      features: [
        'Publish your own schedule',
        'Share your private schedule via a unique link',
        'View player availability',
        'Filter by location, court, and time',
        'Challenge other players',

        'Receive and manage challenges',
        'Court information access',
        'Chat with challenged players',
      ],
      buttonText: 'Get Started',
      icon: <ScheduleIcon />,
      featured: false,
      available: true,
    },
    {
      id: 'premium',
      name: 'Premium Player',
      price: 'Coming Soon',
      period: '',
      description: 'Enhanced features for serious players',
      features: [
        'Everything in Free Player',
        'Recurent availability schedules',
        'Advanced player statistics',
        'Match history tracking',
        'Priority booking alerts',
        'Enhanced profile features',
        'Calendar sync integration',
      ],
      buttonText: 'Coming Soon',
      icon: <StarIcon />,
      featured: true,
      available: false,
    },
    {
      id: 'coach',
      name: 'Coach & Professional',
      price: 'Coming Soon',
      period: '',
      description: 'Designed for tennis coaches and professionals',
      features: [
        'Everything in Premium Player',
        'Student/client management',
        'Lesson scheduling tools',
        'Payment processing integration',
        'Professional profile badge',
        'Group lesson coordination',
        'Coach Analytics dashboard',
      ],
      buttonText: 'Coming Soon',
      icon: <BusinessIcon />,
      featured: false,
      available: false,
    },
  ];

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="md">
          <HeroTitle>
            Choose Your Plan
          </HeroTitle>
          <HeroSubtitle>
            Start for free and upgrade when you need more features
          </HeroSubtitle>
        </Container>
      </HeroSection>

      {/* Plans Section */}
      <PlansSection>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, sm: 2 }} justifyContent="center">
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <PlanCard featured={plan.featured ? true : undefined}>
                  <CardContent sx={{ 
                    p: { xs: 1.5, sm: 2 },
                  }}>
                    {false && plan.featured && (
                      <FeaturedBadge>
                        <StarIcon />
                        Most Popular
                      </FeaturedBadge>
                    )}
                    
                    <PlanIconWrapper>
                      {plan.icon}
                    </PlanIconWrapper>
                    
                    <PlanTitle>
                      {plan.name}
                    </PlanTitle>
                    
                    <PlanDescription>
                      {plan.description}
                    </PlanDescription>
                    
                    <PriceSection>
                      <Price>
                        {plan.price}
                      </Price>
                      {plan.period && (
                        <PricePeriod>
                          {plan.period}
                        </PricePeriod>
                      )}
                    </PriceSection>
                  </CardContent>

                  <Divider />

                  <FeatureList>
                    {plan.features.map((feature, index) => (
                      <FeatureItem key={index}>
                        <FeatureIcon>
                          <CheckIcon />
                        </FeatureIcon>
                        <FeatureText primary={feature} />
                      </FeatureItem>
                    ))}
                  </FeatureList>

                  <CardActions sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                    {plan.available ? (
                      plan.id === 'free' ? (
                        <SecondaryButton onClick={handleGetStarted}>
                          {plan.buttonText}
                        </SecondaryButton>
                      ) : (
                        <PrimaryButton onClick={handleGetStarted}>
                          {plan.buttonText}
                        </PrimaryButton>
                      )
                    ) : (
                      <ComingSoonButton disabled>
                        {plan.buttonText}
                      </ComingSoonButton>
                    )}
                  </CardActions>
                </PlanCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PlansSection>

      <Footer />

      {/* Login Dialog */}
      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default SubscriptionPlansPage;